import { cn } from "@plasma/utils/cn";
import { Monitor, Moon, Sun } from "lucide-react";

import { useTheme } from "./theme-provider";
import { useSidebar } from "./ui/sidebar";

function getThemeIcon(theme: string) {
  if (theme === "light") {
    return Sun;
  }
  if (theme === "dark") {
    return Moon;
  }
  return Monitor;
}

function getNextTheme(theme: string): "light" | "dark" | "system" {
  if (theme === "light") {
    return "dark";
  }
  if (theme === "dark") {
    return "system";
  }
  return "light";
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Get the icon for the current theme
  const CurrentIcon = getThemeIcon(theme);

  // When collapsed, show only the current theme icon (clickable to cycle through themes)
  if (isCollapsed) {
    const nextTheme = getNextTheme(theme);
    return (
      <button
        aria-label={`Current: ${theme} mode. Click to switch to ${nextTheme}`}
        className="border-input bg-background text-foreground hover:bg-muted inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors"
        onClick={() => setTheme(nextTheme)}
        type="button"
      >
        <CurrentIcon className="size-4" />
      </button>
    );
  }

  // When expanded, show all three options
  return (
    <div className="border-input bg-background inline-flex items-center rounded-lg border p-1">
      <button
        aria-label="Light mode"
        className={cn(
          "inline-flex h-8 flex-1 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors",
          theme === "light"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => setTheme("light")}
        type="button"
      >
        <Sun className="size-4" />
      </button>
      <button
        aria-label="Dark mode"
        className={cn(
          "inline-flex h-8 flex-1 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors",
          theme === "dark"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => setTheme("dark")}
        type="button"
      >
        <Moon className="size-4" />
      </button>
      <button
        aria-label="System mode"
        className={cn(
          "inline-flex h-8 flex-1 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors",
          theme === "system"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => setTheme("system")}
        type="button"
      >
        <Monitor className="size-4" />
      </button>
    </div>
  );
}
